import type { TemplatePageBody } from '@messenger/types'

type BasePage = {
  id: string
  page_number: number
  grid_start_point_top: number
  grid_start_point_left: number
  rows: number
  columns: number
}

type EditorPage = {
  pageProperties?: Record<string, unknown>
  product?: { orderPageId?: string; items?: unknown[] } & Record<
    string,
    unknown
  >
} & Record<string, unknown>

const CASE_IMAGE_PLACEHOLDER = {
  url: 'https://placehold.co/163x163',
  edits: {},
}

/** A case photo belongs to the case, not the layout, so a layout keeps a
 * placeholder in its place. */
const withoutCaseImage = (item: unknown): unknown => {
  const image = item as {
    type?: string
    properties?: { subtype?: string }
  } | null
  if (image?.type !== 'image' || image.properties?.subtype !== 'caseImage')
    return item
  return { ...(item as object), image: { ...CASE_IMAGE_PLACEHOLDER } }
}

/**
 * The pages a layout save sends: one per line page that the editor holds, in
 * page order, each naming the order page it is copied from. The JSON leaves
 * out what belongs to the line (its page and page-set ids and name, the proof
 * watermarks) and swaps case photos for a placeholder; the server stamps the
 * new page's ids in. The turnover run id stays.
 */
export const templatePageBodies = (
  basePages: readonly BasePage[],
  editorPages: readonly EditorPage[],
): TemplatePageBody[] =>
  [...basePages]
    .sort((a, b) => a.page_number - b.page_number)
    .flatMap((page) => {
      const json = editorPages.find(
        (editorPage) => editorPage.product?.orderPageId === page.id,
      )
      if (!json) return []
      const {
        orderPageId: _orderPageId,
        pageId: _pageId,
        watermarks: _watermarks,
        items = [],
        ...product
      } = json.product ?? {}
      const {
        pageSet: _pageSet,
        pageSetId: _pageSetId,
        ...pageProperties
      } = json.pageProperties ?? {}
      return [
        {
          gridStartPointTop: page.grid_start_point_top,
          gridStartPointLeft: page.grid_start_point_left,
          rows: page.rows,
          columns: page.columns,
          pageNumber: page.page_number,
          jsonTemplate: JSON.stringify({
            ...json,
            pageProperties,
            product: { ...product, items: items.map(withoutCaseImage) },
          }),
          sourceOrderPageId: page.id,
        },
      ]
    })
